import { notify, useModal } from '@billy/design-system'
import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { queryClient } from '../../../../../config/queryClient'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { InvoiceDocumentType } from '../../../enums/invoiceDocumentType'
import { useDeleteInvoice } from '../../../hooks/useDeleteInvoice'
import { useFetchInvoice } from '../../../hooks/useFetchInvoice'
import { useInvalidateSalesReports } from '../../../hooks/useInvalidateSalesReports'
import { getCancelDepositInvoiceEligibility } from '../../../utils/depositInvoices/getCancelDepositInvoiceEligibility'
import { getInvoiceQueryKey } from '../../../utils/getInvoiceQueryKey'
import { getTrackingDocumentType, TrackingDocumentType } from '../../../utils/getTrackingDocumentType'
import { BlockCancelInvoiceActionVariant } from '../../BlockCancelInvoiceActionModal/enums/blockCancelInvoiceActionVariant'
import { getBlockCancelInvoiceActionModalId } from '../../BlockCancelInvoiceActionModal/utils/getBlockCancelInvoiceActionModalId'
import { CancelInvoiceAction } from '../enums/cancelInvoiceAction'

interface ContextState {
  closeCancelInvoiceModal: () => void
  invoiceId: string
  isCancelBlockedByFinalInvoice: boolean
  isCreateCreditNoteAction: boolean
  isDeleteInvoiceAction: boolean
  isDeletingInvoice: boolean
  handleDeleteInvoice: () => void
  onConfirmCreditNote: (() => void) | undefined
  openBlockCancelInvoiceActionModal: (variant: BlockCancelInvoiceActionVariant) => void
  setActionType: Dispatch<SetStateAction<CancelInvoiceAction | undefined>>
  shouldDisplayCancelInvoiceModalFooter: boolean
  trackingDocumentType: TrackingDocumentType | undefined
}

const CancelInvoiceModalContext = createContext<ContextState | undefined>(undefined)

interface CancelInvoiceModalContextProviderProps {
  children?: ReactNode
  documentType: InvoiceDocumentType
  invoiceId: string
  modalId: string
  onConfirmCreditNote?: () => void
}

export const CancelInvoiceModalContextProvider = ({
  children,
  documentType,
  invoiceId,
  modalId,
  onConfirmCreditNote,
}: CancelInvoiceModalContextProviderProps): ReactElement => {
  const { t } = useTranslation()
  const [actionType, setActionType] = useState<CancelInvoiceAction | undefined>(undefined)
  const { close: closeCancelInvoiceModal } = useModal(modalId)
  const { open: openBlockCreditNoteModal } = useModal(
    getBlockCancelInvoiceActionModalId(invoiceId, BlockCancelInvoiceActionVariant.CreditNote),
  )
  const { open: openBlockVoidModal } = useModal(
    getBlockCancelInvoiceActionModalId(invoiceId, BlockCancelInvoiceActionVariant.Void),
  )
  const { invalidateSalesReports } = useInvalidateSalesReports()
  const { invoice } = useFetchInvoice(documentType, invoiceId)

  const { isCancelBlockedByFinalInvoice } = getCancelDepositInvoiceEligibility(invoice)
  const trackingDocumentType = getTrackingDocumentType({ billingStage: invoice?.billingStage, documentType })

  const openBlockCancelInvoiceActionModal = useCallback(
    (variant: BlockCancelInvoiceActionVariant) => {
      closeCancelInvoiceModal()

      if (variant === BlockCancelInvoiceActionVariant.CreditNote) {
        openBlockCreditNoteModal()
      } else {
        openBlockVoidModal()
      }
    },
    [closeCancelInvoiceModal, openBlockCreditNoteModal, openBlockVoidModal],
  )

  const { deleteInvoice, isProcessing: isDeletingInvoice } = useDeleteInvoice({
    onSuccess: () => {
      invalidateSalesReports()
      queryClient.invalidateQueries(getInvoiceQueryKey(invoiceId, documentType))
      queryClient.invalidateQueries(QueryKeys.InvoicesZervant)
      queryClient.invalidateQueries(QueryKeys.InvoicesSummary)
      queryClient.invalidateQueries(QueryKeys.Quote)
      queryClient.invalidateQueries(QueryKeys.Quotes)

      notify({
        id: NotificationKeys.ExternalInvoiceDeleted,
        message: t('external_invoices.view.void.notification.success'),
        variant: 'success',
      })
      closeCancelInvoiceModal()
    },
    onError: () => {
      notify({
        id: NotificationKeys.ExternalInvoiceDeleted,
        message: t('error_saving'),
        variant: 'error',
      })
    },
  })

  const isCreateCreditNoteAction = actionType === CancelInvoiceAction.CreateCreditNote
  const isDeleteInvoiceAction = actionType === CancelInvoiceAction.DeleteInvoice
  const shouldDisplayCancelInvoiceModalFooter = isCreateCreditNoteAction || isDeleteInvoiceAction

  const handleDeleteInvoice = useCallback(() => {
    deleteInvoice(Number(invoiceId))
  }, [deleteInvoice, invoiceId])

  const contextValue = useMemo(
    () => ({
      closeCancelInvoiceModal,
      handleDeleteInvoice,
      invoiceId,
      isCancelBlockedByFinalInvoice,
      isCreateCreditNoteAction,
      isDeleteInvoiceAction,
      isDeletingInvoice,
      onConfirmCreditNote,
      openBlockCancelInvoiceActionModal,
      setActionType,
      shouldDisplayCancelInvoiceModalFooter,
      trackingDocumentType,
    }),
    [
      closeCancelInvoiceModal,
      handleDeleteInvoice,
      invoiceId,
      isCancelBlockedByFinalInvoice,
      isCreateCreditNoteAction,
      isDeleteInvoiceAction,
      isDeletingInvoice,
      onConfirmCreditNote,
      openBlockCancelInvoiceActionModal,
      setActionType,
      shouldDisplayCancelInvoiceModalFooter,
      trackingDocumentType,
    ],
  )

  return <CancelInvoiceModalContext.Provider value={contextValue}>{children}</CancelInvoiceModalContext.Provider>
}

export const useCancelInvoiceModal = () => {
  const context = useContext(CancelInvoiceModalContext)

  if (!context) {
    throw new Error('CancelInvoiceModalContextProvider is missing in the module!')
  }

  return context
}
